import { trackApiError } from '@/helpers/analytics'
import { getAccessToken, removeAccessToken } from '@/helpers/cookies'

import { API_CONFIG } from './api'

export class ApiError extends Error {
  status: number
  code: string

  constructor(message: string, status: number, code: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

function buildHeaders(
  customHeaders?: Record<string, string>,
  includeContentType = false
): HeadersInit {
  const headers: Record<string, string> = {
    tenant: API_CONFIG.tenantCode,
    ...customHeaders,
  }

  if (includeContentType) {
    headers['Content-Type'] = 'application/json'
  }

  const token = getAccessToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

async function handleResponse(response: Response, endpoint?: string) {
  if (!response.ok) {
    if (response.status === 401) {
      removeAccessToken()
      trackApiError(endpoint ?? response.url, 401, 'session_expired')
      throw new ApiError('Session expired', 401, 'session_expired')
    }

    const body = await response.json().catch(() => ({}))
    const errorCode = body.code || 'generic_error'
    const errorMessage = body.message || 'Request failed'

    trackApiError(
      endpoint ?? response.url,
      response.status,
      errorCode,
      errorMessage
    )

    throw new ApiError(errorMessage, response.status, errorCode)
  }

  if (response.status === 204) {
    return
  }

  return response.json()
}

export const apiClient = {
  get: (path: string, customHeaders?: Record<string, string>) =>
    fetch(`${API_CONFIG.domain}${path}`, {
      headers: buildHeaders(customHeaders),
    }).then(response => handleResponse(response, path)),

  post: (
    path: string,
    body?: unknown,
    customHeaders?: Record<string, string>
  ) =>
    fetch(`${API_CONFIG.domain}${path}`, {
      method: 'POST',
      headers: buildHeaders(customHeaders, !!body),
      body: body ? JSON.stringify(body) : undefined,
    }).then(response => handleResponse(response, path)),

  put: (path: string, body?: unknown, customHeaders?: Record<string, string>) =>
    fetch(`${API_CONFIG.domain}${path}`, {
      method: 'PUT',
      headers: buildHeaders(customHeaders, !!body),
      body: body ? JSON.stringify(body) : undefined,
    }).then(response => handleResponse(response, path)),
}

export function parseJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1]
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    [...window.atob(base64)]
      // eslint-disable-next-line unicorn/prefer-code-point
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}
